import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import CustomerLayout from '../components/CustomerLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  Search,
  Filter,
  Grid3X3,
  List,
  Star,
  Heart,
  ShoppingCart,
  SlidersHorizontal,
  Store
} from 'lucide-react';
import { formatPrice, calculateDiscount } from '../lib/utils';
import { useCart } from '../contexts/CartContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';

export default function ProductsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  // Default to 'list' view for better mobile experience
  const [viewMode, setViewMode] = useState('list');
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    collection: searchParams.get('collection') || '',
    category: searchParams.get('category') || '',  // Support category name filter from HomePage
    sortBy: searchParams.get('sort') || 'newest',
    priceRange: [0, 1000],
    onSale: searchParams.get('on_sale') === 'true'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    hasMore: true
  });

  const { addToCart } = useCart();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // SEO: Update meta tags for products page
  useEffect(() => {
    const searchTerm = filters.search || searchParams.get('search') || '';
    const category = filters.category || searchParams.get('category') || '';
    
    if (searchTerm) {
      document.title = `${searchTerm} - Search Results | NammaHut`;
    } else if (category) {
      document.title = `${category} - Traditional Indian Products | NammaHut`;
    } else {
      document.title = 'All Products - Traditional Indian Sweets, Snacks & More | NammaHut';
    }
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      if (searchTerm) {
        metaDescription.content = `Find ${searchTerm} at NammaHut. Shop authentic traditional Indian products with free delivery across India.`;
      } else {
        metaDescription.content = 'Browse all traditional Indian products at NammaHut. Shop sweets, snacks, dry fruits, coffee, rice & millets from verified sellers. Free delivery!';
      }
    }
  }, [filters.search, filters.category]);

  useEffect(() => {
    fetchCollections();
  }, []);

  useEffect(() => {
    fetchProducts();
    updateURLParams();
  }, [filters, pagination.page]);

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/collections`);
      setCollections(response.data);
    } catch (error) {
      console.error('Failed to fetch collections:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.collection) params.append('collection_id', filters.collection);
      if (filters.category) params.append('category', filters.category);  // Support category name filter
      params.append('skip', ((pagination.page - 1) * pagination.limit).toString());
      params.append('limit', pagination.limit.toString());
      
      const response = await axios.get(`${API_URL}/api/products?${params}`);
      let newProducts = response.data.map(p => ({
        ...p,
        id: p.id || p._id  // Ensure id field exists
      }));

      // Client-side filtering for price range and sale
      newProducts = newProducts.filter(product => {
        const price = product.sale_price || product.price;
        const inPriceRange = price >= filters.priceRange[0] && price <= filters.priceRange[1];
        const matchesSale = !filters.onSale || (filters.onSale && product.sale_price);
        return inPriceRange && matchesSale;
      });
      
      if (pagination.page === 1) {
        setProducts(newProducts);
      } else {
        setProducts(prev => [...prev, ...newProducts]);
      }
      
      setPagination(prev => ({
        ...prev,
        total: newProducts.length,
        hasMore: newProducts.length === pagination.limit
      }));
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const updateURLParams = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.collection) params.set('collection', filters.collection);
    if (filters.category) params.set('category', filters.category);  // Preserve category filter
    if (filters.sortBy !== 'newest') params.set('sort', filters.sortBy);
    if (filters.onSale) params.set('on_sale', 'true');
    setSearchParams(params);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  const handleAddToCart = async (productId, e) => {
    e.preventDefault();
    e.stopPropagation();
    await addToCart(productId, 1);
  };

  const loadMore = () => {
    setPagination(prev => ({ ...prev, page: prev.page + 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      sortBy: 'newest',
      priceRange: [0, 1000],
      onSale: false
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setSearchParams({});
  };

  const FilterSidebar = () => (
    <div className="space-y-6">
      {/* Search */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Search</h3>
        <form onSubmit={handleSearch}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search products..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="pl-10"
              data-testid="products-search-input"
            />
          </div>
        </form>
      </div>

      {/* Categories */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Categories</h3>
        <div className="space-y-2">
          <button
            onClick={() => handleFilterChange('collection', '')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
              !filters.collection ? 'bg-emerald-50 text-emerald-600 font-medium' : 'hover:bg-gray-50 text-gray-700'
            }`}
            data-testid="collection-all"
          >
            All Categories
          </button>
          {collections.map((collection) => (
            <button
              key={collection.id || collection._id}
              onClick={() => handleFilterChange('collection', collection.id || collection._id)}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                filters.collection === (collection.id || collection._id) ? 'bg-emerald-100 text-emerald-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={`collection-${collection.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {collection.name}
            </button>
          ))}
        </div>
      </div>


      {/* Price Range */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Price Range</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              placeholder="Min"
              value={filters.priceRange[0]}
              onChange={(e) => handleFilterChange('priceRange', [parseInt(e.target.value) || 0, filters.priceRange[1]])}
              className="flex-1"
            />
            <span className="text-gray-500">-</span>
            <Input
              type="number"
              placeholder="Max"
              value={filters.priceRange[1]}
              onChange={(e) => handleFilterChange('priceRange', [filters.priceRange[0], parseInt(e.target.value) || 1000])}
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {/* Sale Items */}
      <div>
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.onSale}
            onChange={(e) => handleFilterChange('onSale', e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            data-testid="on-sale-filter"
          />
          <span className="text-gray-700">Sale Items Only</span>
        </label>
      </div>

      {/* Clear Filters */}
      <Button 
        variant="outline" 
        onClick={clearFilters} 
        className="w-full"
        data-testid="clear-filters-button"
      >
        Clear All Filters
      </Button>
    </div>
  );

  const ProductCard = ({ product }) => {
    const productId = product?.id || product?._id;
    // Use slug for SEO-friendly URLs
    const productUrl = product?.slug || productId;
    return (
      <Link 
        to={`/products/${productUrl}`} 
        className={`product-card group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden ${viewMode === 'list' ? 'flex flex-row' : 'block'}`}
        data-testid={`product-${productId}`}
      >
        <div className={`${viewMode === 'list' ? 'w-48 h-48' : 'aspect-square'} overflow-hidden relative`}>
          <img 
            src={product.images?.[0] || 'https://via.placeholder.com/300x300/E8F5E9/2E7D32?text=No+Image'} 
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          
          {/* Discount Badge - More Prominent */}
          {product.sale_price && (
            <div className="absolute top-2 left-2 bg-red-600 text-white px-3 py-1 rounded-full font-bold text-sm shadow-lg">
              {calculateDiscount(product.price, product.sale_price)}% OFF
            </div>
          )}

          {/* Product Origin - Cute Art */}
          {product.region_name && (
            <div className="absolute bottom-2 left-2 bg-orange-500 text-white px-2 py-1 rounded-lg text-xs flex items-center gap-1 shadow-md">
              <span>📍</span>
              <span className="font-medium">{product.region_name}</span>
            </div>
          )}
        
        {/* Quick Actions */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Button
            size="icon"
            variant="secondary"
            className="w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow-lg"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toast.success('Added to wishlist!');
            }}
            data-testid={`wishlist-${product.id}`}
          >
            <Heart className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      <div className={`p-4 ${viewMode === 'list' ? 'flex-1' : ''}`}>
        <h3 className="font-semibold text-base mb-2 line-clamp-2 group-hover:text-green-700 transition-colors">
          {product.name}
        </h3>
        <p className="text-gray-600 text-xs mb-3 line-clamp-2">{product.description}</p>
        
        {/* Variants indicator */}
        {product.variants && product.variants.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {product.variants.slice(0, 3).map((v, i) => (
              <span key={i} className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">
                {v.name || `${v.weight}g`}
              </span>
            ))}
            {product.variants.length > 3 && (
              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                +{product.variants.length - 3} more
              </span>
            )}
          </div>
        )}
        
        <div className="flex items-center mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star 
                key={i} 
                className={`w-3 h-3 ${i < 4 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
              />
            ))}
          </div>
          <span className="text-xs text-gray-500 ml-2">(24)</span>
        </div>
        
        <div className="flex items-center justify-between mb-3">
          <div className="flex flex-col">
            <span className="text-xl font-bold text-green-700">
              {product.variants && product.variants.length > 0 
                ? `From ${formatPrice(Math.min(...product.variants.map(v => v.sale_price || v.price)))}`
                : formatPrice(product.sale_price || product.price)
              }
            </span>
            {!product.variants?.length && product.sale_price && (
              <span className="text-sm text-gray-500 line-through">
                {formatPrice(product.price)}
              </span>
            )}
          </div>
          
          {product.inventory > 0 ? (
            <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
              In Stock
            </Badge>
          ) : (
            <Badge variant="destructive" className="text-xs">
              Out of Stock
            </Badge>
          )}
        </div>

        {/* Small Compact Buttons */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-8 text-xs"
            onClick={(e) => handleAddToCart(product.id, e)}
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingCart className="w-3 h-3 mr-1" />
            Add to Cart
          </Button>
          <Button
            size="sm"
            className="flex-1 h-8 text-xs bg-green-700 hover:bg-green-800"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddToCart(product.id, e);
              setTimeout(() => navigate('/cart'), 500);
            }}
          >
            Buy Now
          </Button>
        </div>
      </div>
    </Link>
  );
};

  // Get current selected category info
  const selectedCategory = filters.category 
    ? collections.find(c => c.name === filters.category)
    : filters.collection 
    ? collections.find(c => (c.id || c._id) === filters.collection)
    : null;

  // Default banners for categories
  const DEFAULT_CATEGORY_BANNERS = {
    'Traditional Sweets': 'https://images.unsplash.com/photo-1589119908995-c6837fa14848?w=1200&h=400&fit=crop',
    'Traditional Snacks': 'https://images.unsplash.com/photo-1599490659213-e2b9527bd087?w=1200&h=400&fit=crop',
    'Handloom & Textiles': 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=1200&h=400&fit=crop',
    'Handicrafts': 'https://images.unsplash.com/photo-1605721911519-3dfeb3be25e7?w=1200&h=400&fit=crop',
    'Spices & Masalas': 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=1200&h=400&fit=crop',
    'Dry fruits & Nuts': 'https://images.unsplash.com/photo-1608797178974-15b35a64ede9?w=1200&h=400&fit=crop',
    'Traditional Coffee Power': 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=1200&h=400&fit=crop',
    'Puja Items': 'https://images.unsplash.com/photo-1604422415379-87eb9b6c4d74?w=1200&h=400&fit=crop',
    'Home Decor': 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1200&h=400&fit=crop',
    'Jewelry & Accessories': 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=1200&h=400&fit=crop',
  };

  const getCategoryBanner = (category) => {
    return category?.banner || DEFAULT_CATEGORY_BANNERS[category?.name] || null;
  };

  return (
    <CustomerLayout categories={collections}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Category Banner */}
        {selectedCategory && getCategoryBanner(selectedCategory) && (
          <div className="mb-6 relative rounded-xl overflow-hidden h-32 md:h-48">
            <img 
              src={getCategoryBanner(selectedCategory)} 
              alt={selectedCategory.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
            <div className="absolute inset-0 flex items-center p-6">
              <div>
                <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">
                  {selectedCategory.name}
                </h1>
                {selectedCategory.description && (
                  <p className="text-white/80 text-sm md:text-base max-w-xl">
                    {selectedCategory.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                {selectedCategory ? selectedCategory.name : 'Products'}
              </h1>
              <p className="text-gray-600 text-sm">Discover traditional products from verified vendors</p>
            </div>
            
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              {/* Sort */}
              <Select value={filters.sortBy} onValueChange={(value) => handleFilterChange('sortBy', value)}>
                <SelectTrigger className="w-48" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="rating">Highest Rated</SelectItem>
                  <SelectItem value="popular">Most Popular</SelectItem>
                </SelectContent>
              </Select>
              
              {/* View Mode */}
              <div className="flex items-center border rounded-lg p-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  data-testid="grid-view-button"
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  data-testid="list-view-button"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Mobile Filter */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="md:hidden">
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <SheetHeader>
                    <SheetTitle className="flex items-center">
                      <SlidersHorizontal className="w-5 h-5 mr-2" />
                      Filters
                    </SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterSidebar />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Desktop Filter Sidebar */}
          <div className="hidden lg:block">
            <div className="bg-white rounded-xl p-6 shadow-lg sticky top-24">
              <div className="flex items-center mb-6">
                <SlidersHorizontal className="w-5 h-5 mr-2 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">Filters</h2>
              </div>
              <FilterSidebar />
            </div>
          </div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            {loading && pagination.page === 1 ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <p className="text-gray-600" data-testid="products-count">
                    Showing {products.length} products
                  </p>
                </div>
                
                <div className={`grid gap-6 ${
                  viewMode === 'grid' 
                    ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3' 
                    : 'grid-cols-1'
                }`}>
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
                
                {products.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
                    <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
                    <Button onClick={clearFilters} data-testid="no-products-clear-filters">
                      Clear Filters
                    </Button>
                  </div>
                )}
                
                {pagination.hasMore && products.length > 0 && (
                  <div className="text-center mt-12">
                    <Button 
                      onClick={loadMore} 
                      disabled={loading}
                      className="btn-primary"
                      data-testid="load-more-button"
                    >
                      {loading ? (
                        <>
                          <LoadingSpinner size="sm" className="mr-2" />
                          Loading...
                        </>
                      ) : (
                        'Load More Products'
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
}
